import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioButton, NioCard, NioSection } from '../../../components';
import ProviderReviewSection from '../../../components/ProviderReviewSection/ProviderReviewSection';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

const hasValue = (value) => {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const renderValue = (value) => {
  if (!hasValue(value)) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const InfoRow = ({ label, value }) => {
  if (!hasValue(value)) return null;
  return (
    <Col xs={12} md={6}>
      <div className="border-bottom border-light pb-2 mb-3">
        <div className="text-muted small text-uppercase">{label}</div>
        <div>{renderValue(value)}</div>
      </div>
    </Col>
  );
};

const formatServicesLine = (p, t) => {
  if (!p) return null;
  const parts = [];
  if (p.sector) parts.push(String(p.sector).trim());
  if (Array.isArray(p.serviceIds)) {
    p.serviceIds.forEach((id) => {
      const s = String(id ?? '').trim();
      if (s && !parts.includes(s)) parts.push(translateServiceLabel(t, s));
    });
  }
  if (p.serviceId) {
    const s = String(p.serviceId).trim();
    if (s && !parts.includes(s)) parts.push(translateServiceLabel(t, s));
  }
  return parts.length ? parts.join(', ') : null;
};

const formatSousServicesLine = (p, t) => {
  if (!p) return null;
  const raw = [p.serviceCategory, p.subSector]
    .map((x) => (x != null ? String(x).trim() : ''))
    .filter(Boolean);
  const uniq = [...new Set(raw)];
  return uniq.length ? uniq.map((item) => translateServiceLabel(t, item)).join(' · ') : null;
};

export default function ProviderDetails() {
  const { t } = useTranslation();
  const { providerId } = useParams();
  const location = useLocation();
  const [provider, setProvider] = useState(location.state?.provider || null);
  const [loading, setLoading] = useState(!location.state?.provider);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) return;
      try {
        setLoading(true);
        const res = await api.getProfile(providerId);
        if (res && res.success && res.data) {
          setProvider((prev) => ({ ...(prev || {}), ...res.data }));
        } else {
          setError(t('providerDetails.notFound'));
        }
      } catch (e) {
        setError(e?.message || t('providerDetails.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [providerId, t]);

  const zoneIntervention = useMemo(() => {
    const p = provider;
    if (!p) return '';
    if (hasValue(p.coverageGuarantees)) return String(p.coverageGuarantees).trim();
    if (hasValue(p.location)) return String(p.location).trim();
    if (p.interventionCity && p.interventionRegion) {
      return `${String(p.interventionCity).trim()} (${String(p.interventionRegion).trim()})`;
    }
    if (hasValue(p.interventionCity)) return String(p.interventionCity).trim();
    if (hasValue(p.interventionRegion)) return String(p.interventionRegion).trim();
    return [p.city, p.region].filter(Boolean).join(', ');
  }, [provider]);

  const servicesLine = useMemo(() => formatServicesLine(provider, t), [provider, t]);
  const sousServicesLine = useMemo(() => formatSousServicesLine(provider, t), [provider, t]);

  const prestationHasAny =
    provider &&
    [servicesLine, sousServicesLine, provider.pricing, zoneIntervention].some((x) => hasValue(x));

  const serviceTitle = location.state?.serviceTitle
    ? translateServiceLabel(t, location.state.serviceTitle)
    : undefined;

  return (
    <AppLayout title={t('providerDetails.pageTitle')} rootClass="layout-1">
      <NioSection className="pt-120 pt-lg-160">
        <Row className="justify-content-center">
          <Col lg={10}>
            <NioCard className="shadow-sm">
              <NioCard.Body className="p-4 p-lg-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mb-0">{t('providerDetails.heading')}</h2>
                  <Link to={location.state?.fromPath || '/services'}>
                    <NioButton className="btn-outline-primary btn-sm" label={t('providerDetails.back')} />
                  </Link>
                </div>

                {loading ? (
                  <div className="text-center text-muted py-4">{t('providerDetails.loading')}</div>
                ) : error || !provider ? (
                  <div className="text-center text-danger py-4">{error || t('providerDetails.notFound')}</div>
                ) : (
                  <>
                    <p className="text-muted mb-4">{t('providerDetails.intro')}</p>
                    {!prestationHasAny ? (
                      <div className="text-muted py-2">{t('providerDetails.noInfo')}</div>
                    ) : (
                      <Row>
                        <InfoRow label={t('providerDetails.service')} value={servicesLine} />
                        <InfoRow label={t('providerDetails.subService')} value={sousServicesLine} />
                        <InfoRow label={t('providerDetails.pricing')} value={provider.pricing} />
                        <InfoRow label={t('providerDetails.zone')} value={zoneIntervention} />
                      </Row>
                    )}

                    <ProviderReviewSection
                      providerId={provider._id || providerId}
                      providerName={[provider.name, provider.lastName].filter(Boolean).join(' ') || provider.businessName}
                      providerEmail={provider.email}
                      serviceId={location.state?.serviceId || provider.serviceId}
                      serviceTitle={serviceTitle}
                    />
                  </>
                )}
              </NioCard.Body>
            </NioCard>
          </Col>
        </Row>
      </NioSection>
    </AppLayout>
  );
}
